import type { BaseHit, Hit } from 'instantsearch.js';
import type { CarouselProduct, Product } from '../../../../codegen/types';
import { getAmount } from '../../../currencyHelper';
import { getCtLocale } from '../../../internationalization';
import { sendDataLayerEvent } from '../../utils/dataLayerInteractions';
import EventNamesType from '../eventNamesType';

type PrimaryCategory = {
  [key: string]: string;
};

type ItemList = {
  itemListId?: string;
  itemListName?: string;
};

export type EventDataList = {
  products?: Product[] | Hit<BaseHit>[];
  carouselProducts?: CarouselProduct[];
  algoliaCarouselProducts?: { objectID: string }[];
  itemList?: ItemList;
  newProducts?: Hit<BaseHit>[];
  baseUrl: string;
  locale?: string;
};

type ExtendedBaseHit = BaseHit & {
  price?: {
    centAmount?: number;
  };
  wasPrice?: {
    centAmount?: number;
  };
  thenPrice?: {
    centAmount?: number;
  };
  images?: string[];
};

const mapProducts = (baseUrl: string, products?: Product[] | Hit<BaseHit>[], ctLocale?: string, itemList?: ItemList) =>
  products?.map((item: BaseHit, index) => {
    const {
      [`name.${ctLocale}`]: name,
      [`mediumColor.${ctLocale}`]: mediumColor,
      [`brand.${ctLocale}`]: brand,
      [`slug.${ctLocale}`]: slug,
      price,
      wasPrice,
      thenPrice,
      objectID,
      primaryCategory = {},
      images,
      objectID: sku,
    } = item as ExtendedBaseHit;
    const priceAmount = +getAmount(price?.centAmount);
    const thenPriceAmount = +getAmount(thenPrice?.centAmount);
    const wasPriceAmount = +getAmount(wasPrice?.centAmount);
    const discountAmount = wasPriceAmount && priceAmount ? +wasPriceAmount - +priceAmount : 0;
    const { [`primaryCategory.${ctLocale}`]: primaryName, key: primaryKey } = primaryCategory as PrimaryCategory;
    const url = baseUrl ? `${baseUrl}/${slug}/${sku}-p` : undefined;
    const imageUrl = images && images.length > 0 ? images[0] : undefined;

    return {
      index,
      quantity: 1,
      item_name: name,
      item_id: objectID,
      price: priceAmount,
      item_brand: brand,
      item_category: primaryName ?? undefined,
      item_category_id: primaryKey ?? undefined,
      item_variant: mediumColor,
      item_list_name: itemList?.itemListName,
      item_list_id: itemList?.itemListId,
      discount: +discountAmount.toFixed(2),
      was_price: thenPriceAmount,
      then_price: wasPriceAmount,
      item_url: url,
      item_image_url: imageUrl,
    };
  });

export const sendLoadMoreItemsEvent = ({ products, newProducts, itemList, locale, baseUrl }: EventDataList) => {
  const ctLocale = getCtLocale(locale);

  sendDataLayerEvent(EventNamesType.load_more_items, {
    ecommerce: {
      item_list_id: itemList?.itemListId,
      item_list_name: itemList?.itemListName,
      items: mapProducts(baseUrl, newProducts, ctLocale, itemList) ?? [],
      allItems: mapProducts(baseUrl, products, ctLocale, itemList) ?? [],
    },
  });
};
